/**
 * Brand marks for the services the Brain connects to.
 *
 * **Inline SVG, not remote images.** A connector row that fetches five logos
 * from vendor CDNs is five requests that can fail, five things that leak which
 * console a user has open, and a row that renders blank behind a proxy. These
 * are a few hundred bytes each and always there.
 *
 * They are simplified marks, drawn to be recognisable at 20–24px rather than
 * to be exact reproductions of the vendors' brand assets. If any of these
 * integrations ever ships to customers, the vendor's own asset and its usage
 * terms are what should be here — that is a legal question, not a drawing one.
 *
 * Each takes `className` so the caller can size it and mute it; the fills are
 * fixed, because a brand mark that inherits `currentColor` is not a brand mark.
 */

type MarkProps = { className?: string; title?: string };

export function GmailMark({ className, title }: MarkProps) {
  return (
    <svg viewBox="0 0 52 40" className={className} role="img" aria-label={title ?? "Gmail"}>
      <path fill="#4285f4" d="M3.64 40h8.18V20.18L0 10.91v25.45C0 38.37 1.63 40 3.64 40z" />
      <path
        fill="#34a853"
        d="M40.18 40h8.18c2.01 0 3.64-1.63 3.64-3.64V10.91l-11.82 9.27V40z"
      />
      <path fill="#fbbc04" d="M40.18 3.64v16.54L52 10.91V5.45c0-5.05-5.77-7.94-9.82-4.91z" />
      <path fill="#ea4335" d="M11.82 20.18V3.64L26 14.27 40.18 3.64v16.54L26 30.82z" />
      <path fill="#c5221f" d="M0 5.45v5.46l11.82 9.27V3.64L9.82.54C5.77-2.49 0 .4 0 5.45z" />
    </svg>
  );
}

export function GoogleDriveMark({ className, title }: MarkProps) {
  return (
    <svg
      viewBox="0 0 87.3 78"
      className={className}
      role="img"
      aria-label={title ?? "Google Drive"}
    >
      <path
        fill="#0066da"
        d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5z"
      />
      <path
        fill="#00ac47"
        d="M43.65 25 29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44A9.06 9.06 0 0 0 0 53h27.45z"
      />
      <path
        fill="#ea4335"
        d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.8l5.85 11.5z"
      />
      <path fill="#00832d" d="M43.65 25 57.4 1.2C56.05.4 54.5 0 52.9 0H34.4c-1.6 0-3.15.45-4.5 1.2z" />
      <path
        fill="#2684fc"
        d="M59.8 53H27.45L13.7 76.8c1.35.8 2.9 1.2 4.5 1.2h51.1c1.6 0 3.15-.45 4.5-1.2z"
      />
      <path
        fill="#ffba00"
        d="M73.4 26.5 60.7 4.5c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.15 28h27.4c0-1.55-.4-3.1-1.2-4.5z"
      />
    </svg>
  );
}

export function WhatsAppMark({ className, title }: MarkProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} role="img" aria-label={title ?? "WhatsApp"}>
      <path
        fill="#25d366"
        d="M12 0a11.9 11.9 0 0 0-10.2 18.1L0 24l6.1-1.8A11.9 11.9 0 1 0 12 0z"
      />
      <path
        fill="#fff"
        d="M8.9 6.6c-.2-.5-.4-.5-.6-.5h-.6c-.2 0-.5.1-.8.4s-1 1-1 2.4 1 2.8 1.2 3 2 3.2 4.9 4.4c2.4 1 2.9.8 3.4.7s1.7-.7 1.9-1.4.2-1.2.2-1.3-.2-.2-.4-.3-1.3-.6-1.5-.7-.4-.1-.5.1-.6.7-.7.9-.3.2-.5.1a6 6 0 0 1-1.8-1.1 6.6 6.6 0 0 1-1.2-1.5c-.1-.2 0-.3.1-.4l.3-.4a1.5 1.5 0 0 0 .2-.4.4.4 0 0 0 0-.4c0-.1-.4-1.3-.6-1.7z"
      />
    </svg>
  );
}

/** Outlook (`plans/outlook-sync.md`): an envelope behind the "O" tile, simplified like the others. */
export function OutlookMark({ className, title }: MarkProps) {
  return (
    <svg viewBox="0 0 48 48" className={className} role="img" aria-label={title ?? "Outlook"}>
      <rect x="16" y="9" width="30" height="30" rx="3" fill="#28a8ea" />
      <path fill="#0a64c8" d="M16 22l15 9 15-9v14a3 3 0 0 1-3 3H19a3 3 0 0 1-3-3z" />
      <rect x="2" y="12" width="24" height="24" rx="3" fill="#0364b8" />
      <path
        fill="#fff"
        fillRule="evenodd"
        d="M14 17.2c-3.8 0-6.4 2.9-6.4 6.8s2.6 6.8 6.4 6.8 6.4-2.9 6.4-6.8-2.6-6.8-6.4-6.8zm0 2.6c2.1 0 3.4 1.7 3.4 4.2s-1.3 4.2-3.4 4.2-3.4-1.7-3.4-4.2 1.3-4.2 3.4-4.2z"
      />
    </svg>
  );
}

export function SharepointMark({ className, title }: MarkProps) {
  return (
    <svg viewBox="0 0 48 48" className={className} role="img" aria-label={title ?? "SharePoint"}>
      <circle cx="27" cy="15" r="13" fill="#036c70" />
      <circle cx="35" cy="27" r="11" fill="#1a9ba1" />
      <circle cx="27" cy="38" r="9" fill="#37c6d0" />
      <rect x="2" y="12" width="24" height="24" rx="3" fill="#03787c" />
      <path
        fill="#fff"
        d="M10.4 26.6c.9.6 2 1 3.1 1 1.2 0 1.8-.4 1.8-1.1 0-.6-.4-.9-2-1.5-2.1-.8-3-1.8-3-3.4 0-2.1 1.7-3.6 4.3-3.6 1.2 0 2.3.3 3 .7v2.3a5 5 0 0 0-2.9-.9c-1 0-1.6.4-1.6 1 0 .6.4.9 1.9 1.5 2.3.8 3.1 1.9 3.1 3.4 0 2.2-1.7 3.6-4.6 3.6-1.3 0-2.5-.3-3.2-.8z"
      />
    </svg>
  );
}
